// GPS Location Restriction Utility
// Target Location: 24.830285°N, 67.0708655°E (MNTX Office - Actual Location)
// Allowed Radius: 200 meters (increased for GPS accuracy tolerance)
// Applies to users with gps_restricted = true

const TARGET_LAT = 24.830285;
const TARGET_LON = 67.0708655;
const ALLOWED_RADIUS_METERS = 200;
const ERROR_MESSAGE = "This database is inaccessible outside permitted zone.";

/**
 * Calculate distance between two GPS coordinates using Haversine formula
 * Returns distance in meters
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
};

/**
 * Check if user is within allowed GPS zone
 * Returns Promise with { allowed: boolean, distance: number, error: string }
 */
export const checkLocationAccess = () => {
  console.log('🌍 checkLocationAccess() called - requesting browser location...');
  
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      console.error('❌ Geolocation API not supported by browser');
      reject({
        allowed: false,
        error: 'GPS not supported by your browser',
        distance: null
      });
      return;
    }

    console.log('📍 Calling navigator.geolocation.getCurrentPosition() - browser should prompt now...');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('✅ GPS permission granted! Received position:', position);
        const userLat = position.coords.latitude;
        const userLon = position.coords.longitude;
        const accuracy = position.coords.accuracy;
        console.log(`📍 Your location: ${userLat}, ${userLon} (accuracy: ±${accuracy}m)`);
        console.log(`🏢 Office location: ${TARGET_LAT}, ${TARGET_LON}`);
        
        const distance = calculateDistance(userLat, userLon, TARGET_LAT, TARGET_LON);
        console.log(`📏 Distance from office: ${distance.toFixed(2)}m (allowed: ${ALLOWED_RADIUS_METERS}m)`);
        
        const allowed = distance <= ALLOWED_RADIUS_METERS;
        
        if (!allowed) {
          console.warn(`❌ Access DENIED - Distance ${distance.toFixed(2)}m exceeds ${ALLOWED_RADIUS_METERS}m limit`);
          resolve({
            allowed: false,
            distance: Math.round(distance),
            latitude: userLat,
            longitude: userLon,
            error: ERROR_MESSAGE
          });
        } else {
          console.log(`✅ Access GRANTED - Within ${ALLOWED_RADIUS_METERS}m radius`);
          resolve({
            allowed: true,
            distance: Math.round(distance),
            latitude: userLat,
            longitude: userLon,
            error: null
          });
        }
      },
      (error) => {
        console.error('❌ GPS error occurred:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        
        let userFriendlyError = ERROR_MESSAGE;
        if (error.code === 1) {
          userFriendlyError = 'Location permission denied. Please allow location access in your browser settings.';
        } else if (error.code === 2) {
          userFriendlyError = 'Location unavailable. Please enable GPS on your device.';
        } else if (error.code === 3) {
          userFriendlyError = 'Location request timed out. Please try again.';
        }
        
        reject({
          allowed: false,
          error: userFriendlyError,
          distance: null,
          gpsError: error.code,
          gpsMessage: error.message
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

/**
 * Start periodic location monitoring
 * Checks location every hour (3600000 ms)
 * Calls onLocationViolation if user goes outside zone or GPS unavailable
 */
export const startLocationMonitoring = (user, onLocationViolation) => {
  console.log('🌍 Starting GPS monitoring (checks every hour)...');

  const checkInterval = setInterval(async () => {
    console.log('🔍 Performing hourly GPS location check...');
    try {
      const result = await checkLocationAccess();
      
      if (!result.allowed) {
        console.warn(`❌ Location violation detected: ${result.distance}m from office`);
        onLocationViolation(result);
      } else {
        console.log(`✅ Location verified: ${result.distance}m from office`);
      }
    } catch (error) {
      console.error('❌ GPS check failed:', error);
      onLocationViolation(error);
    }
  }, 60 * 60 * 1000); // Check every 1 hour (3600000 ms)

  return checkInterval;
};

/**
 * Stop location monitoring
 */
export const stopLocationMonitoring = (intervalId) => {
  if (intervalId) {
    clearInterval(intervalId);
    console.log('🛑 GPS monitoring stopped');
  }
};
